import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { Canvas, useLoader } from '@react-three/fiber'
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js'
import { MapControls } from '@react-three/drei'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faLayerGroup, faEye, faBars } from '@fortawesome/free-solid-svg-icons'
import { useAlert } from '../contexts/AlertContext'
import config from '../config'
import { validateRegionData, normalizeRegionData } from '../utils/validation'


function Cell({ index, baseColor, shape, fillOpacity, selectedIndex, onSelect, isStageSelected }) {
  const [hovered, hover] = useState(false)
  const isSelected = selectedIndex === index
  useEffect(() => {
    document.body.style.cursor = hovered ? 'pointer' : 'default'
    return () => {
      document.body.style.cursor = 'default'
    }
  }, [hovered])
  return (
    <mesh onPointerOver={() => hover(true)} onPointerOut={() => hover(false)} onClick={(e) => { e.stopPropagation(); onSelect(index) }}>
      <meshBasicMaterial color={isStageSelected ? '#2b8cff' : (isSelected ? 'orange' : (hovered ? 'hotpink' : baseColor))} opacity={fillOpacity} depthWrite={false} transparent />
      <shapeBufferGeometry args={[shape]} />
    </mesh>
  )
}

function SvgEditor({ url, selectedIndex, onSelect, regions, stageSelectedIds, stageColorize, stageColorMap, stageViewTarget, onlySelectedIndex = null, statusColors = null }) {
  const { paths } = useLoader(SVGLoader, url)
  const shapes = useMemo(
    () => paths.flatMap((p) => p.toShapes(true).map((shape) => ({ shape, color: p.color, fillOpacity: p.userData.style.fillOpacity }))),
    [paths]
  )

  const statusToColor = (status) => {
    // Use statusColors from props if available, otherwise fallback to defaults
    if (statusColors && statusColors[status]) {
      return statusColors[status]
    }
    switch (status) {
      case 'AVAILABLE': return '#4CAF50'
      case 'SOLD': return '#F44336'
      case 'LOCKED': return '#FFD700'
      default: return '#FFD700'
    }
  }

  const getRegionForIndex = (idx) => {
    // Try new stable key path-<pathIndex>-shape-<shapeIndex>
    const byStable = regions.find((r) => r.svgPathId === `path-${idx}-shape-0`) // most common case single shape
    if (byStable) return byStable
    const byId = regions.find((r) => r.svgPathId === `path-${idx}`)
    if (byId) return byId
    if (idx >= 0 && idx < regions.length) return regions[idx]
    return null
  }

  const enriched = useMemo(() => {
    return shapes.map((s, idx) => {
      const region = getRegionForIndex(idx)
      let baseColor = s.color
      if (region) {
        if (stageColorize && region.name) {
          const c = stageColorMap && stageColorMap[region.name]
          baseColor = c || '#9ca3af'
        } else {
          const hasDetails = Boolean(region.name || region.lotNumber || region.lotSize || region.price || region.notes)
          baseColor = hasDetails ? statusToColor(region.status) : '#D3D3D3'
        }
      }
      // Hide regions not in the viewed stage when stageViewTarget is set
      const matchesStage = !stageViewTarget || (region && region.name === stageViewTarget)
      const matchesOnly = onlySelectedIndex == null || idx === onlySelectedIndex
      const visible = matchesStage && matchesOnly
      return { ...s, baseColor, visible }
    })
  }, [shapes, regions, stageColorize, stageColorMap, stageViewTarget, onlySelectedIndex, statusColors])

  const ref = useRef()
  useLayoutEffect(() => {
    const sphere = new THREE.Box3().setFromObject(ref.current).getBoundingSphere(new THREE.Sphere())
    ref.current.position.set(-sphere.center.x, -sphere.center.y, 0)
  }, [])

  return (
    <group ref={ref} scale={[1, -1, 1]}>
      {enriched.filter(p => p.visible !== false).map((props, index) => {
        const region = getRegionForIndex(index)
        const isStageSelected = Boolean(region?.id && stageSelectedIds && stageSelectedIds.has(region.id))
        return (
          <Cell key={props.shape.uuid} index={index} selectedIndex={selectedIndex} onSelect={onSelect} isStageSelected={isStageSelected} {...props} />
        )
      })}
    </group>
  )
}

export default function MapEditor({ map, onMapUpdated, onPublished, initialIndex = null, embedded = false, focusSelectedOnly = false }) {
  const { alert } = useAlert()
  const [regions, setRegions] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedIndex, setSelectedIndex] = useState(null)
  const [selectedRegion, setSelectedRegion] = useState(null)
  const [form, setForm] = useState({ 
    name: '', 
    lotNumber: '', 
    lotSize: '', 
    lotSizeValue: '', 
    lotSizeUnit: 'm²', 
    status: 'AVAILABLE', 
    price: '', 
    notes: '', 
    frontage: '', 
    frontageValue: '', 
    frontageUnit: 'm', 
    aspect: '',
    group: '',
    subGroup: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [isPublished, setIsPublished] = useState(false)
  const [panelMode, setPanelMode] = useState('region') // 'region' | 'stage'
  const [selectedRegionIdsForStage, setSelectedRegionIdsForStage] = useState(new Set())
  const [stageNameInput, setStageNameInput] = useState('')
  const [stagesCatalog, setStagesCatalog] = useState([])
  const [stageEditTarget, setStageEditTarget] = useState(null) // when editing specific stage
  const [stageViewTarget, setStageViewTarget] = useState(null) // when viewing specific stage
  const [stageActionMode, setStageActionMode] = useState('create') // 'create', 'assign', 'view', 'delete'
  const [stageLockLoading, setStageLockLoading] = useState({}) // track loading state for each stage
  const [showDisplayLabelModal, setShowDisplayLabelModal] = useState(null) // stageName when showing modal, null otherwise
  const [displayLabelInput, setDisplayLabelInput] = useState('') // current input for display label
  // Sales groups management
  const groupColorPalette = ['#2563eb', '#16a34a', '#f59e0b', '#ef4444', '#8b5cf6', '#0ea5e9', '#22c55e', '#eab308', '#f97316', '#14b8a6', '#a855f7', '#f43f5e']
  const [showSalesGroupModal, setShowSalesGroupModal] = useState(false)
  const [pendingStatusChange, setPendingStatusChange] = useState(null) // { field: 'status', value: 'SOLD' }
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [renamingGroup, setRenamingGroup] = useState(null) // { oldName, newName }
  const [editingGroupColor, setEditingGroupColor] = useState(null) // { groupName, color } or { groupName, subGroupName, color } for sub-groups
  const [expandedGroups, setExpandedGroups] = useState(new Set()) // Track which groups are expanded to show sub-groups
  const [showCreateSubGroupModal, setShowCreateSubGroupModal] = useState(null) // groupName when showing sub-group creation, null otherwise
  const [newSubGroupName, setNewSubGroupName] = useState('')
  const [renamingSubGroup, setRenamingSubGroup] = useState(null) // { groupName, oldName, newName }
  const [colorUpdateTrigger, setColorUpdateTrigger] = useState(0) // Force re-render when colors change
  const [unassignedGroups, setUnassignedGroups] = useState(new Set()) // Track groups created but not yet assigned to any lot
  const [unassignedSubGroups, setUnassignedSubGroups] = useState(new Map()) // Track sub-groups by group: Map<groupName, Set<subGroupName>>
  // Status colors management
  const [statusColors, setStatusColors] = useState({
    AVAILABLE: '#4CAF50',
    SOLD: '#F44336',
    LOCKED: '#FFD700'
  })
  const [editingStatusColor, setEditingStatusColor] = useState(null) // { status: 'AVAILABLE', color: '#4CAF50' }
  
  // Get group color from database (customProps) or localStorage or generate from hash
  // Prioritizes database colors so they sync across devices
  // Using useCallback to ensure it's reactive to regions changes
  const getGroupColor = useCallback((groupName, subGroupName = null) => {
    if (!groupName) return '#6b7280'
    
    // Handle combined format "group::subGroup" for backward compatibility
    let actualGroupName = groupName
    let actualSubGroupName = subGroupName
    if (typeof groupName === 'string' && groupName.includes('::') && !subGroupName) {
      const parts = groupName.split('::')
      actualGroupName = parts[0]
      actualSubGroupName = parts[1] || null
    }
    
    // First, try to find color in any region with this group/sub-group (from database)
    // This ensures colors sync across devices even if the current region doesn't have it
    if (regions && Array.isArray(regions)) {
      const matchingRegion = regions.find(r => {
        if (actualSubGroupName) {
          return r.customProps?.group === actualGroupName && r.customProps?.subGroup === actualSubGroupName
        } else {
          return r.customProps?.group === actualGroupName
        }
      })
      
      if (matchingRegion) {
        if (actualSubGroupName && matchingRegion.customProps?.subGroupColor) {
          return matchingRegion.customProps.subGroupColor
        }
        if (!actualSubGroupName && matchingRegion.customProps?.groupColor) {
          return matchingRegion.customProps.groupColor
        }
      }
    }
    
    // Fallback to localStorage (for backward compatibility)
    const groupNameStr = String(actualGroupName)
    const key = actualSubGroupName ? `${actualGroupName}::${actualSubGroupName}` : actualGroupName
    const stored = localStorage.getItem(`group_color_${key}`)
    if (stored) return stored
    
    // Generate color from hash
    let hash = 0
    for (let i = 0; i < groupNameStr.length; i++) {
      hash = ((hash << 5) - hash) + groupNameStr.charCodeAt(i)
    }
    const colorIndex = Math.abs(hash) % groupColorPalette.length
    return groupColorPalette[colorIndex]
  }, [regions])
  
  // Set group color in localStorage
  const setGroupColor = (groupName, color) => {
    if (groupName && color) {
      localStorage.setItem(`group_color_${groupName}`, color)
      setColorUpdateTrigger(prev => prev + 1) // Trigger re-render
    }
  }
  
  // Extract sales groups from regions and include unassigned groups
  const getSalesGroups = useMemo(() => {
    // Extract unique groups and sub-groups from regions
    const existingGroups = new Map() // Map<groupName, { name, color, subGroups: Map<subGroupName, { name, color }> }>
    regions.forEach(region => {
      if (region.customProps?.group) {
        const groupName = region.customProps.group
        if (!existingGroups.has(groupName)) {
          existingGroups.set(groupName, {
            name: groupName,
            color: getGroupColor(groupName),
            subGroups: new Map()
          })
        }
        
        // Add sub-group if exists
        if (region.customProps?.subGroup) {
          const subGroupName = region.customProps.subGroup
          const group = existingGroups.get(groupName)
          if (!group.subGroups.has(subGroupName)) {
            group.subGroups.set(subGroupName, {
              name: subGroupName,
              color: getGroupColor(groupName, subGroupName)
            })
          }
        }
      }
    })
    
    // Add unassigned groups (groups created but not yet assigned to any lot)
    unassignedGroups.forEach(groupName => {
      if (!existingGroups.has(groupName)) {
        existingGroups.set(groupName, {
          name: groupName,
          color: getGroupColor(groupName),
          subGroups: new Map()
        })
      }
    })
    
    // Add unassigned sub-groups
    unassignedSubGroups.forEach((subGroupSet, groupName) => {
      if (!existingGroups.has(groupName)) {
        existingGroups.set(groupName, {
          name: groupName,
          color: getGroupColor(groupName),
          subGroups: new Map()
        })
      }
      const group = existingGroups.get(groupName)
      subGroupSet.forEach(subGroupName => {
        if (!group.subGroups.has(subGroupName)) {
          group.subGroups.set(subGroupName, {
            name: subGroupName,
            color: getGroupColor(groupName, subGroupName)
          })
        }
      })
    })
    
    // Convert subGroups Maps to arrays and sort
    const groupsArray = Array.from(existingGroups.values()).map(group => ({
      ...group,
      subGroups: Array.from(group.subGroups.values()).sort((a, b) => a.name.localeCompare(b.name))
    }))
    
    // Return all groups sorted alphabetically
    return groupsArray.sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
  }, [regions, colorUpdateTrigger, unassignedGroups, unassignedSubGroups, getGroupColor])

  const svgUrl = map?.svgUrl?.startsWith('http') ? map.svgUrl : `${config.API_BASE}${map.svgUrl}`

  const fetchRegions = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(config.getApiUrl(`/regions/map/${map.id}`), {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        setRegions(data)
      }
      
    } catch (err) {
      console.error('Failed to fetch regions:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchPublishedStatus = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(config.getApiUrl(`/maps/${map.id}`), { headers: { Authorization: `Bearer ${token}` } })
      if (res.ok) {
        const data = await res.json()
        const published = Array.isArray(data?.versions) && data.versions.some(v => v.published)
        setIsPublished(Boolean(published))
      }
    } catch (e) { /* noop */ }
  }

  const fetchStages = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(config.getApiUrl(`/maps/${map.id}/stages`), { headers: { Authorization: `Bearer ${token}` } })
      if (res.ok) {
        const data = await res.json()
        setStagesCatalog(Array.isArray(data?.stages) ? data.stages : [])
      }
    } catch (_) {}
  }

  useEffect(() => {
    setLoading(true)
    setSelectedIndex(null)
    setSelectedRegion(null)
    // Don't reset form in embedded mode - let the embedded useEffect handle it
    if (!embedded) {
      setForm({ name: '', lotNumber: '', lotSize: '', status: 'AVAILABLE', price: '', notes: '', frontage: '', aspect: '' })
    }
    fetchRegions()
    fetchPublishedStatus()
    fetchStages()
  }, [map.id, embedded])

  useEffect(() => {
    if (selectedIndex == null) {
      setSelectedRegion(null)
      return
    }
    
    // Use the same logic as SvgEditor for consistency
    const getRegionForIndex = (idx) => {
      // Try new stable key path-<pathIndex>-shape-<shapeIndex>
      const byStable = regions.find((r) => r.svgPathId === `path-${idx}-shape-0`) // most common case single shape
      if (byStable) return byStable
      const byId = regions.find((r) => r.svgPathId === `path-${idx}`)
      if (byId) return byId
      if (idx >= 0 && idx < regions.length) return regions[idx]
      return null
    }
    
    const region = getRegionForIndex(selectedIndex)
    // console.log('[MapEditor] select index', selectedIndex, 'regions', regions.length, 'embedded:', embedded)
    // console.log('[MapEditor] matched region', region, 'will set form:', region ? {
    //   name: region.name,
    //   lotNumber: region.lotNumber,
    //   lotSize: region.lotSize,
    //   status: region.status,
    //   price: region.price,
    //   notes: region.notes
    // } : 'null')
    setSelectedRegion(region || null)
    if (region) {
      // Parse lot size and frontage values
      const lotSizeMatch = (region.lotSize || '').match(/^(\d+(?:\.\d+)?)\s*(.+)$/)
      const frontageMatch = (region.customProps?.frontage || '').match(/^(\d+(?:\.\d+)?)\s*(.+)$/)
      
      setForm({
        name: region.name || '',
        lotNumber: region.lotNumber || '',
        lotSize: region.lotSize || '',
        lotSizeValue: lotSizeMatch ? lotSizeMatch[1] : '',
        lotSizeUnit: lotSizeMatch ? lotSizeMatch[2] : 'm²',
        status: region.status || 'AVAILABLE',
        price: region.price || '',
        notes: region.notes || '',
        frontage: region.customProps?.frontage || '',
        frontageValue: frontageMatch ? frontageMatch[1] : '',
        frontageUnit: frontageMatch ? frontageMatch[2] : 'm',
        aspect: region.customProps?.aspect || '',
        group: region.customProps?.group || '',
        subGroup: region.customProps?.subGroup || ''
      })
    } else {
      setForm({ 
        name: '', 
        lotNumber: '', 
        lotSize: '', 
        lotSizeValue: '', 
        lotSizeUnit: 'm²', 
        status: 'AVAILABLE', 
        price: '', 
        notes: '', 
        frontage: '', 
        frontageValue: '', 
        frontageUnit: 'm', 
        aspect: '',
        group: '',
        subGroup: ''
      })
    }
  }, [selectedIndex, regions])

  const handleRegionFieldChange = (field, value) => {
    // Check if lot is assigned to a stage - if not, prevent editing details
    // Allow changing the stage name field itself, but not other details
    if (field !== 'name' && !form.name && !selectedRegion?.name) {
      alert.warning('Please assign this lot to a stage first before filling details. Go to "Stage Selection" tab to assign the lot to a stage.')
      setPanelMode('stage')
      return
    }
    
    // Allow group assignment for all statuses (AVAILABLE, SOLD, LOCKED)
    // If clicking on status dropdown, allow changing group
    if (field === 'status' && form.status === value) {
      // Same status selected - allow changing group
      setShowSalesGroupModal(true)
      return
    }
    
    setForm((prev) => {
      const newForm = { ...prev, [field]: value }
      
      // Combine value and unit fields for lot size and frontage
      if (field === 'lotSizeValue' || field === 'lotSizeUnit') {
        const lotSizeValue = field === 'lotSizeValue' ? value : prev.lotSizeValue
        const lotSizeUnit = field === 'lotSizeUnit' ? value : prev.lotSizeUnit
        if (lotSizeValue && lotSizeUnit) {
          newForm.lotSize = `${lotSizeValue} ${lotSizeUnit}`
        } else {
          newForm.lotSize = ''
        }
      }
      
      if (field === 'frontageValue' || field === 'frontageUnit') {
        const frontageValue = field === 'frontageValue' ? value : prev.frontageValue
        const frontageUnit = field === 'frontageUnit' ? value : prev.frontageUnit
        if (frontageValue && frontageUnit) {
          newForm.frontage = `${frontageValue} ${frontageUnit}`
        } else {
          newForm.frontage = ''
        }
      }
      
      return newForm
    })
    
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
    
    // Real-time validation for specific fields
    if (field === 'lotNumber' || field === 'lotSize' || field === 'frontage' || field === 'price' || field === 'notes' || field === 'name') {
      const validation = validateRegionData({ [field]: value })
      if (!validation.isValid && validation.errors[field]) {
        setFormErrors(prev => ({ ...prev, [field]: validation.errors[field] }))
      }
    }
  }
  
  const handleSalesGroupSelect = (groupName, subGroupName = null) => {
    if (pendingStatusChange) {
      setForm((prev) => {
        const newForm = { 
          ...prev, 
          [pendingStatusChange.field]: pendingStatusChange.value, 
          group: groupName,
          subGroup: subGroupName || ''
        }
        return newForm
      })
      setPendingStatusChange(null)
    } else {
      // If not pending status change, just update the group and sub-group
      setForm((prev) => {
        return { 
          ...prev, 
          group: groupName,
          subGroup: subGroupName || (groupName === prev.group ? prev.subGroup : '')
        }
      })
    }
    
    // Don't remove from unassigned groups/sub-groups here - only remove when actually saved
    // This allows the group/sub-group to remain in the list if user doesn't save
    
    setShowSalesGroupModal(false)
    setShowCreateGroupModal(false)
  }
  
  const handleSalesGroupCancel = () => {
    setPendingStatusChange(null)
    setShowSalesGroupModal(false)
    setShowCreateGroupModal(false)
    setNewGroupName('')
    setRenamingGroup(null)
  }
  
  const handleCreateNewGroup = () => {
    const trimmedName = newGroupName.trim()
    
    if (!trimmedName) {
      alert.warning('Please enter a group name')
      return
    }
    
    // Check if group already exists
    const exists = getSalesGroups.some(g => g.name.toLowerCase() === trimmedName.toLowerCase())
    if (exists) {
      alert.warning('A group with this name already exists')
      return
    }
    
    // Add to unassigned groups so it appears in the list immediately
    setUnassignedGroups(prev => new Set([...prev, trimmedName]))
    
    // Select the new group
    handleSalesGroupSelect(trimmedName)
    setNewGroupName('')
  }
  
  const handleCreateNewSubGroup = (groupName) => {
    const trimmedName = newSubGroupName.trim()
    
    if (!trimmedName) {
      alert.warning('Please enter a sub-group name')
      return
    }
    
    // Check if sub-group already exists in this group
    const group = getSalesGroups.find(g => g.name === groupName)
    if (group && group.subGroups.some(sg => sg.name.toLowerCase() === trimmedName.toLowerCase())) {
      alert.warning('A sub-group with this name already exists in this group')
      return
    }
    
    // Add to unassigned sub-groups so it appears in the list immediately
    setUnassignedSubGroups(prev => {
      const newMap = new Map(prev)
      const subGroupSet = newMap.get(groupName) || new Set()
      subGroupSet.add(trimmedName)
      newMap.set(groupName, subGroupSet)
      return newMap
    })
    
    // Select the new sub-group
    handleSalesGroupSelect(groupName, trimmedName)
    setNewSubGroupName('')
    setShowCreateSubGroupModal(null)
  }
  
  const toggleGroupExpansion = (groupName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupName)) {
        newSet.delete(groupName)
      } else {
        newSet.add(groupName)
      }
      return newSet
    })
  }
  
  const handleRenameGroup = async (oldName, newName) => {
    const trimmedNewName = newName.trim()
    
    if (!trimmedNewName || trimmedNewName === oldName) {
      setRenamingGroup(null)
      return
    }
    
    // Check if new name already exists
    const exists = getSalesGroups.some(g => g.name.toLowerCase() === trimmedNewName.toLowerCase() && g.name !== oldName)
    if (exists) {
      alert.warning('A group with this name already exists')
      setRenamingGroup(null)
      return
    }
    
    // Check if this is an unassigned group (not yet in any region)
    const isUnassigned = unassignedGroups.has(oldName)
    const regionsToUpdate = regions.filter(r => r.customProps?.group === oldName)
    
    // Migrate color from old name to new name
    const oldColor = localStorage.getItem(`group_color_${oldName}`)
    if (oldColor) {
      localStorage.setItem(`group_color_${trimmedNewName}`, oldColor)
      localStorage.removeItem(`group_color_${oldName}`)
    }
    
    // If it's an unassigned group, just update the unassigned groups set and form
    if (isUnassigned && regionsToUpdate.length === 0) {
      // Update unassigned groups
      setUnassignedGroups(prev => {
        const newSet = new Set(prev)
        newSet.delete(oldName)
        newSet.add(trimmedNewName)
        return newSet
      })
      
      // Update form if current lot has this group
      if (form.group === oldName) {
        setForm((prev) => ({ ...prev, group: trimmedNewName }))
      }
      
      // Trigger color update to refresh group list
      setColorUpdateTrigger(prev => prev + 1)
      setRenamingGroup(null)
      alert.success(`Renamed group "${oldName}" to "${trimmedNewName}"`)
      return
    }
    
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        alert.error('Authentication required')
        setRenamingGroup(null)
        return
      }
      
      
      // Optimistically update local regions state immediately for instant UI update
      if (regionsToUpdate.length > 0) {
        // Update regions state immediately - this will trigger getSalesGroups to recalculate
        setRegions(prevRegions => 
          prevRegions.map(r => {
            if (r.customProps?.group === oldName) {
              return {
                ...r,
                customProps: {
                  ...r.customProps,
                  group: trimmedNewName
                }
              }
            }
            return r
          })
        )
        
        // Update form if current lot has this group
        if (form.group === oldName) {
          setForm((prev) => ({ ...prev, group: trimmedNewName }))
        }
        
        // Update unassigned groups if this group was unassigned
        if (isUnassigned) {
          setUnassignedGroups(prev => {
            const newSet = new Set(prev)
            newSet.delete(oldName)
            newSet.add(trimmedNewName)
            return newSet
          })
        }
        
        // Trigger color update to refresh group list immediately
        setColorUpdateTrigger(prev => prev + 1)
        
        // Clear rename state immediately so UI updates
        setRenamingGroup(null)
        
        const updates = regionsToUpdate.map(r => {
          // Only pass the group field - backend will merge with existing customProps
          return {
            id: r.id,
            name: r.name || '',
            lotNumber: r.lotNumber || '',
            lotSize: r.lotSize || '',
            status: r.status || 'AVAILABLE',
            price: r.price || null,
            notes: r.notes || '',
            group: trimmedNewName // Backend will merge this with existing customProps
          }
        })
        
        
        const res = await fetch(`${config.API_BASE}/regions/bulk`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ updates })
        })
        
        if (!res.ok) {
          // Rollback on error
          await fetchRegions()
          const text = await res.text().catch(() => '')
          console.error(`[Rename Group] Failed: ${res.status} - ${text}`)
          alert.error(`Failed to rename group (${res.status}). ${text}`)
          setRenamingGroup(null)
          return
        }
        
        const result = await res.json().catch(() => ({}))
        
        // Refresh regions to ensure sync with backend (this will also update the group list)
        await fetchRegions()
        alert.success(`Renamed group "${oldName}" to "${trimmedNewName}"`)
      } else {
        // Even if no regions, migrate color and trigger update
        setColorUpdateTrigger(prev => prev + 1)
        setRenamingGroup(null)
        alert.success(`Group name updated (no lots assigned to this group)`)
      }
    } catch (e) {
      // Rollback on error - refresh from backend
      await fetchRegions()
      console.error('[Rename Group] Error:', e)
      alert.error(`Error renaming group: ${e.message}`)
      setRenamingGroup(null)
    }
  }
  
  const handleChangeGroupColor = async (groupName, color, subGroupName = null) => {
    if (!color || !color.match(/^#[0-9A-Fa-f]{6}$/)) {
      alert.warning('Please enter a valid hex color (e.g., #FF0000)')
      return
    }
    if (!map || !regions) {
      alert.error('No map or regions available')
      return
    }

    const key = subGroupName ? `${groupName}::${subGroupName}` : groupName
    // Save to localStorage for backward compatibility
    setGroupColor(key, color)
    
    // Find all regions that belong to this group/sub-group
    const regionsToUpdate = regions.filter(region => {
      if (subGroupName) {
        // For sub-groups, match both group and sub-group
        return region.customProps?.group === groupName && region.customProps?.subGroup === subGroupName
      } else {
        // For groups, match group (regardless of sub-group)
        return region.customProps?.group === groupName
      }
    })

    if (regionsToUpdate.length > 0) {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          alert.error('Authentication required')
          return
        }

        // Update all regions with the color in customProps
        const updates = regionsToUpdate.map(region => {
          const customProps = { ...(region.customProps || {}) }
          if (subGroupName) {
            // Store sub-group color
            customProps.subGroupColor = color
          } else {
            // Store group color
            customProps.groupColor = color
          }
          return {
            id: region.id,
            customProps: customProps
          }
        })

        const response = await fetch(config.getApiUrl('/regions/bulk'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ updates })
        })

        if (!response.ok) {
          throw new Error('Failed to update group color in database')
        }

        // Refresh regions from database to ensure we have the latest data
        await fetchRegions()

        // Publish the map after updating colors so changes are visible in the viewer
        try {
          const publishResponse = await fetch(config.getApiUrl(`/regions/${map.id}/publish`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            }
          })

          if (!publishResponse.ok) {
            console.warn('Failed to publish map after color update')
          } else {
            // Update published status
            setIsPublished(true)
            // Notify parent component if callback exists
            if (onMapUpdated) onMapUpdated()
            if (onPublished) onPublished()
          }
        } catch (publishError) {
          console.error('Publish error:', publishError)
          // Don't fail the whole operation if publish fails
        }

        setEditingGroupColor(null)
        setColorUpdateTrigger(prev => prev + 1)
        const displayName = subGroupName ? `${groupName} > ${subGroupName}` : groupName
        alert.success(`Color updated for ${displayName} and synced across all devices`)
      } catch (error) {
        console.error('Failed to update group color:', error)
        alert.error('Failed to save color to database. It was saved locally only.')
      }
    } else {
      // No regions found, but still save to localStorage for future use
      setEditingGroupColor(null)
      setColorUpdateTrigger(prev => prev + 1)
      const displayName = subGroupName ? `${groupName} > ${subGroupName}` : groupName
      alert.success(`Color saved for ${displayName} (will apply when lots are assigned)`)
    }
  }
  
  const handleRenameSubGroup = async (groupName, oldName, newName) => {
    const trimmedNewName = newName.trim()
    
    if (!trimmedNewName || trimmedNewName === oldName) {
      setRenamingSubGroup(null)
      return
    }
    
    // Check if new name already exists in this group
    const group = getSalesGroups.find(g => g.name === groupName)
    if (group && group.subGroups.some(sg => sg.name.toLowerCase() === trimmedNewName.toLowerCase() && sg.name !== oldName)) {
      alert.warning('A sub-group with this name already exists in this group')
      setRenamingSubGroup(null)
      return
    }
    
    // Check if this is an unassigned sub-group
    const subGroupSet = unassignedSubGroups.get(groupName)
    const isUnassigned = subGroupSet && subGroupSet.has(oldName)
    const regionsToUpdate = regions.filter(r => r.customProps?.group === groupName && r.customProps?.subGroup === oldName)
    
    // Migrate color from old name to new name
    const oldColorKey = `${groupName}::${oldName}`
    const newColorKey = `${groupName}::${trimmedNewName}`
    const oldColor = localStorage.getItem(`group_color_${oldColorKey}`)
    if (oldColor) {
      localStorage.setItem(`group_color_${newColorKey}`, oldColor)
      localStorage.removeItem(`group_color_${oldColorKey}`)
    }
    
    // If it's an unassigned sub-group, just update the unassigned sub-groups map and form
    if (isUnassigned && regionsToUpdate.length === 0) {
      // Update unassigned sub-groups
      setUnassignedSubGroups(prev => {
        const newMap = new Map(prev)
        const subGroupSet = newMap.get(groupName) || new Set()
        subGroupSet.delete(oldName)
        subGroupSet.add(trimmedNewName)
        newMap.set(groupName, subGroupSet)
        return newMap
      })
      
      // Update form if current lot has this sub-group
      if (form.group === groupName && form.subGroup === oldName) {
        setForm((prev) => ({ ...prev, subGroup: trimmedNewName }))
      }
      
      // Trigger color update to refresh group list
      setColorUpdateTrigger(prev => prev + 1)
      setRenamingSubGroup(null)
      alert.success(`Renamed sub-group "${oldName}" to "${trimmedNewName}"`)
      return
    }
    
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        alert.error('Authentication required')
        setRenamingSubGroup(null)
        return
      }
      
      // Optimistically update local regions state immediately for instant UI update
      if (regionsToUpdate.length > 0) {
        // Update regions state immediately
        setRegions(prevRegions => 
          prevRegions.map(r => {
            if (r.customProps?.group === groupName && r.customProps?.subGroup === oldName) {
              return {
                ...r,
                customProps: {
                  ...r.customProps,
                  subGroup: trimmedNewName
                }
              }
            }
            return r
          })
        )
        
        // Update form if current lot has this sub-group
        if (form.group === groupName && form.subGroup === oldName) {
          setForm((prev) => ({ ...prev, subGroup: trimmedNewName }))
        }
        
        // Update unassigned sub-groups if this sub-group was unassigned
        if (isUnassigned) {
          setUnassignedSubGroups(prev => {
            const newMap = new Map(prev)
            const subGroupSet = newMap.get(groupName) || new Set()
            subGroupSet.delete(oldName)
            subGroupSet.add(trimmedNewName)
            newMap.set(groupName, subGroupSet)
            return newMap
          })
        }
        
        // Trigger color update to refresh group list immediately
        setColorUpdateTrigger(prev => prev + 1)
        
        // Update backend
        const updates = regionsToUpdate.map(r => ({
          id: r.id,
          customProps: {
            ...r.customProps,
            subGroup: trimmedNewName
          }
        }))
        
        const response = await fetch('/regions/bulk', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ updates })
        })
        
        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to rename sub-group' }))
          throw new Error(error.error || 'Failed to rename sub-group')
        }
        
        setRenamingSubGroup(null)
        alert.success(`Renamed sub-group "${oldName}" to "${trimmedNewName}"`)
      } else {
        setRenamingSubGroup(null)
      }
    } catch (error) {
      console.error('Error renaming sub-group:', error)
      alert.error(error.message || 'Failed to rename sub-group')
      // Revert optimistic update by refreshing regions
      fetchRegions()
    }
  }

  const saveSelectedRegion = async () => {
    // Allow creating even if no existing region matched; require a selection
    if (selectedIndex == null) return
    // Require at least one stage to be created first
    if (!stagesCatalog || stagesCatalog.length === 0) {
      alert.warning('Please create a stage first. Redirecting to Stage Selection...')
      setPanelMode('stage')
      return
    }
    
    // Check if lot is assigned to a stage - require stage assignment before saving details
    if (!form.name || !form.name.trim()) {
      alert.warning('Please assign this lot to a stage first before saving details. Go to "Stage Selection" tab to assign the lot to a stage.')
      setPanelMode('stage')
      return
    }
    
    // Validate form data before saving
    const validation = validateRegionData(form)
    if (!validation.isValid) {
      setFormErrors(validation.errors)
      alert.error('Please fix the validation errors before saving.')
      return
    }
    
    // Normalize the data
    const normalization = normalizeRegionData(form)
    if (!normalization.isValid) {
      setFormErrors(normalization.errors)
      alert.error('Please fix the validation errors before saving.')
      return
    }
    
    try {
      const token = localStorage.getItem('token')
      const payload = {
        name: normalization.normalizedData.name,
        lotNumber: normalization.normalizedData.lotNumber,
        lotSize: normalization.normalizedData.lotSize,
        status: normalization.normalizedData.status,
        price: normalization.normalizedData.price,
        notes: normalization.normalizedData.notes,
        customProps: JSON.stringify({
          frontage: normalization.normalizedData.frontage,
          aspect: normalization.normalizedData.aspect,
          group: form.group || null,
          subGroup: form.subGroup || null
        })
      }
      let res
      if (selectedRegion && selectedRegion.id) {
        res = await fetch(`${config.API_BASE}/regions/${selectedRegion.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload)
        })
      } else {
        // Create new region mapping for this shape index
        const svgPathId = `path-${selectedIndex}`
        res = await fetch(`${config.API_BASE}/regions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ mapId: map.id, svgPathId, ...payload })
        })
      }
      if (!res.ok) {
        const errText = await res.text().catch(() => '')
        console.error('[MapEditor] save failed', res.status, errText)
        alert.error(`Save failed (${res.status}). ${errText || ''}`)
        return
      }
      let saved = null
      try { saved = await res.json() } catch (_) {}
      // console.log('[MapEditor] save ok', saved)
      
      // If a group was saved, remove it from unassigned groups (it's now in regions)
      if (form.group) {
        setUnassignedGroups(prev => {
          const newSet = new Set(prev)
          newSet.delete(form.group)
          return newSet
        })
      }
      
      // If a sub-group was saved, remove it from unassigned sub-groups
      if (form.group && form.subGroup) {
        setUnassignedSubGroups(prev => {
          const newMap = new Map(prev)
          const subGroupSet = newMap.get(form.group)
          if (subGroupSet) {
            const newSubGroupSet = new Set(subGroupSet)
            newSubGroupSet.delete(form.subGroup)
            if (newSubGroupSet.size === 0) {
              newMap.delete(form.group)
            } else {
              newMap.set(form.group, newSubGroupSet)
            }
          }
          return newMap
        })
      }
      
      await fetchRegions()
      alert.success('Saved')
    } catch (e) {
      console.error('Failed to save region', e)
      alert.error('Save error. See console for details.')
    }
  }

  const publishMap = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${config.API_BASE}/regions/${map.id}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        onMapUpdated && onMapUpdated()
        alert.success('Published successfully')
        if (onPublished) onPublished()
        setIsPublished(true)
      }
    } catch (e) {
      console.error('Failed to publish', e)
    }
  }

  const unpublishMap = async () => {
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${config.API_BASE}/regions/${map.id}/unpublish`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        alert.error(`Unpublish failed (${res.status}). ${text}`)
        return
      }
      const data = await res.json().catch(() => ({}))
      if (data.unpublished === 0) {
        alert.warning('No published version to unpublish')
      } else {
        alert.success('Unpublished successfully')
      }
      onMapUpdated && onMapUpdated()
      if (onPublished) onPublished()
      setIsPublished(false)
    } catch (e) {
      console.error('Failed to unpublish', e)
      alert.error('Unpublish error. See console for details.')
    }
  }

  // Derived stages list from regions (using name as stage)
  const stages = useMemo(() => {
    const bucket = {}
    regions.forEach((r) => {
      const key = (r?.name && String(r.name).trim()) || null
      if (!key) return
      if (!bucket[key]) bucket[key] = 0
      bucket[key]++
    })
    const sorted = Object.keys(bucket).sort((a, b) => {
      const na = Number(a), nb = Number(b)
      const aNum = !isNaN(na), bNum = !isNaN(nb)
      if (aNum && bNum) return na - nb
      if (aNum) return -1
      if (bNum) return 1
      return a.localeCompare(b)
    })
    return sorted
  }, [regions])

  const toggleStageSelectionForRegion = (regionId) => {
    setSelectedRegionIdsForStage((prev) => {
      const next = new Set(prev)
      if (next.has(regionId)) next.delete(regionId)
      else next.add(regionId)
      return next
    })
  }

  // Check if a region is already assigned to the current editing stage
  const isRegionInCurrentStage = (regionId) => {
    if (!stageEditTarget) return false
    const region = regions.find(r => r.id === regionId)
    return region && region.name === stageEditTarget
  }

  // Get regions count for each stage
  const getStageRegionCounts = () => {
    const counts = {}
    stagesCatalog.forEach(stage => {
      const stageName = typeof stage === 'string' ? stage : (stage && stage.name ? stage.name : 'Unknown')
      if (stageName && stageName !== 'Unknown') {
        counts[stageName] = regions.filter(r => r.name === stageName).length
      }
    })
    return counts
  }

  // Check if we can create a new stage (previous stage must have regions)
  const canCreateNewStage = () => {
    if (stagesCatalog.length === 0) return true // Can create first stage
    const stageCounts = getStageRegionCounts()
    const lastStage = stagesCatalog[stagesCatalog.length - 1]
    const lastStageName = typeof lastStage === 'string' ? lastStage : (lastStage && lastStage.name ? lastStage.name : 'Unknown')
    return lastStageName !== 'Unknown' && stageCounts[lastStageName] > 0
  }

  const clearStageSelection = () => setSelectedRegionIdsForStage(new Set())

  const assignStageToSelected = async (stageName, groupName = null) => {
    const targetStage = (stageName && String(stageName).trim()) || (stageEditTarget || '')
    if (!targetStage || selectedRegionIdsForStage.size === 0) {
      alert.warning('Select a stage name and at least one lot.')
      return
    }
    try {
      const token = localStorage.getItem('token')
      
      // Separate regions that are already in this stage (to remove) vs new ones (to add)
      const regionsToRemove = []
      const regionsToAdd = []
      
      Array.from(selectedRegionIdsForStage).forEach((id) => {
        // Stage assignment only
          if (isRegionInCurrentStage(id)) {
            regionsToRemove.push({ id, name: null, group: null }) // Remove from stage
          } else {
            regionsToAdd.push({ id, name: targetStage, group: null }) // Add to stage
        }
      })
      
      // Check if removing regions would make the stage empty
      if (regionsToRemove.length > 0) {
          const stageCounts = getStageRegionCounts()
          const currentCount = stageCounts[targetStage] || 0
          const wouldBeEmpty = currentCount - regionsToRemove.length <= 0
          
          if (wouldBeEmpty) {
            alert.warning(`Cannot remove all lots from Stage ${targetStage}. Each stage must have at least 1 lot.`)
            return
        }
      }
      
      const updates = [...regionsToRemove, ...regionsToAdd]
      const res = await fetch(`${config.API_BASE}/regions/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ updates })
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        alert.error(`Assignment failed (${res.status}). ${text}`)
        return
      }
      await fetchRegions()
      clearStageSelection()
      setStageEditTarget(null)
      setStageNameInput('')
      // Redirect to assign home page after successful assignment
      setStageActionMode('assign')
      
      const removedCount = regionsToRemove.length
      const addedCount = regionsToAdd.length
      let message = ''
      const target = `Stage ${targetStage}`
      if (removedCount > 0 && addedCount > 0) {
        message = `Removed ${removedCount} lot${removedCount !== 1 ? 's' : ''} from ${target} and added ${addedCount} lot${addedCount !== 1 ? 's' : ''} to ${target}`
      } else if (removedCount > 0) {
        message = `Removed ${removedCount} lot${removedCount !== 1 ? 's' : ''} from ${target}`
      } else {
        message = `Added ${addedCount} lot${addedCount !== 1 ? 's' : ''} to ${target}`
      }
      alert.success(message)
    } catch (e) {
      console.error('Failed to assign stage', e)
      alert.error('Error assigning stage. See console for details.')
    }
  }

  // Deterministic color per stage (admin-only visualization)
  const stagePalette = ['#2563eb','#16a34a','#f59e0b','#ef4444','#8b5cf6','#0ea5e9','#22c55e','#eab308','#f97316','#14b8a6','#a855f7','#f43f5e']
  const getStageColor = (name) => {
    if (!name) return '#9ca3af'
    let hash = 0
    for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash) + name.charCodeAt(i)
    const idx = Math.abs(hash) % stagePalette.length
    return stagePalette[idx]
  }

  const createStage = async () => {
    if (!canCreateNewStage()) {
      const stageCounts = getStageRegionCounts()
      const lastStage = stagesCatalog[stagesCatalog.length - 1]
      const lastStageName = typeof lastStage === 'string' ? lastStage : (lastStage && lastStage.name ? lastStage.name : 'Unknown')
      alert.warning(`Cannot create Stage ${stagesCatalog.length + 1}. Stage ${lastStageName} must have at least one lot assigned first.`)
      return
    }
    
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(config.getApiUrl(`/maps/${map.id}/stages`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}) // auto-increment
      })
      if (!res.ok) {
        const t = await res.text().catch(()=>'')
        alert.error(`Create stage failed (${res.status}). ${t}`)
        return
      }
      const data = await res.json().catch(()=>({}))
      setStagesCatalog(Array.isArray(data?.stages) ? data.stages : [])
    } catch (e) {
      console.error('Create stage error', e)
    }
  }

  const renameStage = async (oldName, newName) => {
    if (!newName || newName === oldName) return
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(config.getApiUrl(`/maps/${map.id}/stages/${encodeURIComponent(oldName)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ newName })
      })
      if (!res.ok) {
        const t = await res.text().catch(()=>'' )
        alert.error(`Rename failed (${res.status}). ${t}`)
        return
      }
      const data = await res.json().catch(()=>({}))
      setStagesCatalog(Array.isArray(data?.stages) ? data.stages : [])
      await fetchRegions() // regions names may have changed
    } catch (e) { console.error('Rename stage error', e) }
  }

  const deleteStage = async (name) => {
    if (!name) return
    
    const stageCounts = getStageRegionCounts()
    if (stageCounts[name] > 0) {
      alert.warning(`Cannot delete Stage ${name}. It has ${stageCounts[name]} lot${stageCounts[name] !== 1 ? 's' : ''} assigned. Remove all lots first.`)
      return
    }
    
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(config.getApiUrl(`/maps/${map.id}/stages/${encodeURIComponent(name)}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!res.ok) {
        const t = await res.text().catch(()=>'' )
        alert.error(`Delete failed (${res.status}). ${t}`)
        return
      }
      const data = await res.json().catch(()=>({}))
      setStagesCatalog(Array.isArray(data?.stages) ? data.stages : [])
    } catch (e) { console.error('Delete stage error', e) }
  }


  const toggleStageLock = async (stageName, isLocked, displayLabel = null) => {
    try {
      setStageLockLoading(prev => ({ ...prev, [stageName]: true }))
      const token = localStorage.getItem('token')
      const body = { isLocked }
      if (displayLabel !== null) {
        body.displayLabel = displayLabel
      }
      const res = await fetch(config.getApiUrl(`/maps/${map.id}/stages/${encodeURIComponent(stageName)}/lock`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      })
      if (!res.ok) {
        const t = await res.text().catch(()=>'')
        alert.error(`Toggle lock failed (${res.status}). ${t}`)
        return
      }
      const data = await res.json().catch(()=>({}))
      setStagesCatalog(Array.isArray(data?.stages) ? data.stages : [])
      setShowDisplayLabelModal(null)
      setDisplayLabelInput('')
    } catch (e) { 
      console.error('Toggle stage lock error', e)
      alert.error('Failed to toggle stage lock')
    } finally {
      setStageLockLoading(prev => ({ ...prev, [stageName]: false }))
    }
  }

  const handleLockStageClick = (stageName, currentIsLocked) => {
    if (currentIsLocked) {
      // If unlocking, no need for display label
      toggleStageLock(stageName, false)
    } else {
      // If locking, show modal to select display label
      const stage = stagesCatalog.find(s => {
        const sName = typeof s === 'string' ? s : (s && s.name ? s.name : 'Unknown')
        return sName === stageName
      })
      const currentLabel = typeof stage === 'object' && stage?.displayLabel ? stage.displayLabel : ''
      setDisplayLabelInput(currentLabel)
      setShowDisplayLabelModal(stageName)
    }
  }

  const handleConfirmDisplayLabel = (stageName) => {
    const label = displayLabelInput.trim() || null
    toggleStageLock(stageName, true, label)
  }

  // Memoized stage color map for admin stage-color visualization
  const stageColorMapMemo = useMemo(() => {
    const map = {}
    ;(stagesCatalog || []).forEach((s) => { 
      const stageName = typeof s === 'string' ? s : (s && s.name ? s.name : 'Unknown')
      if (stageName && stageName !== 'Unknown') {
        map[stageName] = getStageColor(stageName) 
      }
    })
    return map
  }, [stagesCatalog])

  // When embedded with a requested initial index, set it once after regions load
  const initializedRef = useRef(false)
  useEffect(() => {
    if (!embedded) return
    if (initialIndex == null) return
    if (initializedRef.current) return
    if (regions.length === 0) return // Wait for regions to load
    
    // console.log('[MapEditor] Setting initial index for embedded mode:', initialIndex, 'regions loaded:', regions.length)
    setSelectedIndex(initialIndex)
    
    // Also populate the form immediately for embedded mode
    // Use the same logic as SvgEditor for consistency
    const getRegionForIndex = (idx) => {
      // Try new stable key path-<pathIndex>-shape-<shapeIndex>
      const byStable = regions.find((r) => r.svgPathId === `path-${idx}-shape-0`) // most common case single shape
      if (byStable) return byStable
      const byId = regions.find((r) => r.svgPathId === `path-${idx}`)
      if (byId) return byId
      if (idx >= 0 && idx < regions.length) return regions[idx]
      return null
    }
    
    const region = getRegionForIndex(initialIndex)
    if (region) {
      // console.log('[MapEditor] Setting form for embedded region:', region)
      // console.log('[MapEditor] Region details:', {
      //   name: region.name,
      //   lotNumber: region.lotNumber,
      //   lotSize: region.lotSize,
      //   status: region.status,
      //   price: region.price,
      //   notes: region.notes,
      //   frontage: region.customProps?.frontage,
      //   aspect: region.customProps?.aspect
      // })
      
      // Parse lot size and frontage values
      const lotSizeMatch = (region.lotSize || '').match(/^(\d+(?:\.\d+)?)\s*(.+)$/)
      const frontageMatch = (region.customProps?.frontage || '').match(/^(\d+(?:\.\d+)?)\s*(.+)$/)
      
      const formData = {
        name: region.name || '',
        lotNumber: region.lotNumber || '',
        lotSize: region.lotSize || '',
        status: region.status || 'AVAILABLE',
        price: region.price || '',
        notes: region.notes || '',
        frontage: region.customProps?.frontage || '',
        aspect: region.customProps?.aspect || ''
      }
      
      // console.log('[MapEditor] Setting form data:', formData)
      setForm(formData)
    }
    
    initializedRef.current = true
  }, [embedded, initialIndex, regions])

  if (loading) return <div style={{ textAlign: 'center', padding: 24 }}>Loading editor...</div>

  return (
    <div className="editor-layout" style={{ 
      display: 'flex', 
      flexDirection: window.innerWidth < 768 ? 'column' : 'row',
      height: embedded ? '70vh' : '100vh',
      overflow: 'hidden'
    }}>
      <div className="editor-canvas" style={{ 
        flex: 1,
        minHeight: window.innerWidth < 768 ? '50vh' : (embedded ? '70vh' : '100vh'),
        position: 'relative'
      }}>
        <Canvas frameloop="demand" orthographic camera={{ position: [0, 0, 50], zoom: 2, up: [0, 0, 1], far: 10000 }}>
          <Suspense fallback={null}>
            <SvgEditor 
              url={svgUrl}
              selectedIndex={selectedIndex}
              onSelect={(idx) => {
                if (panelMode === 'stage') {
                  // In stage mode, toggle the clicked region in selection set
                  const targetPathId = `path-${idx}`
                  const region = regions.find((r) => r.svgPathId === targetPathId) || (idx >= 0 && idx < regions.length ? regions[idx] : null)
                  if (region?.id) toggleStageSelectionForRegion(region.id)
                  setSelectedIndex(idx)
                } else {
                  setSelectedIndex(idx)
                }
              }}
              regions={regions}
              stageSelectedIds={selectedRegionIdsForStage}
              stageColorize={panelMode === 'stage'}
              stageColorMap={stageColorMapMemo}
              stageViewTarget={stageViewTarget}
              onlySelectedIndex={focusSelectedOnly && selectedIndex != null ? selectedIndex : null}
              statusColors={statusColors}
            />
          </Suspense>
          <MapControls enableRotate={false} />
        </Canvas>
        {panelMode === 'region' && (
          <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.55)', color: 'white', padding: '6px 10px', borderRadius: 6, fontSize: 12 }}>
            {selectedIndex == null ? 'Click a region to edit' : `Editing index #${selectedIndex} ${selectedRegion ? '(matched)' : '(new)'}`}
          </div>
        )}
        {panelMode === 'stage' && (
          <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.55)', color: 'white', padding: '6px 10px', borderRadius: 6, fontSize: 12 }}>
            Stage Selection: click lots to select/deselect
          </div>
        )}
        <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.4)', color: 'white', padding: '4px 8px', borderRadius: 4, fontSize: 11 }}>
          regions: {regions.length}
        </div>
      </div>

      <div className="editor-side" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: window.innerWidth < 768 ? 8 : 12,
        width: window.innerWidth < 768 ? '100%' : '400px',
        minWidth: window.innerWidth < 768 ? '100%' : '350px',
        maxWidth: window.innerWidth < 768 ? '100%' : '500px',
        height: window.innerWidth < 768 ? '40vh' : (embedded ? '70vh' : '100vh'),
        overflowY: 'auto',
        padding: window.innerWidth < 768 ? '8px' : '16px',
        background: '#f8fafc'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          gap: 8,
          marginBottom: window.innerWidth < 768 ? 8 : 0
        }}>
          <div style={{ 
            display: 'flex', 
            gap: window.innerWidth < 768 ? 4 : 8,
            width: window.innerWidth < 768 ? '100%' : 'auto'
          }}>
            <button 
              type="button" 
              className={`btn btn-sm ${panelMode==='region'?'btn-primary':'btn-ghost'}`} 
              onClick={()=>setPanelMode('region')}
              style={{
                flex: window.innerWidth < 768 ? 1 : 'none',
                minHeight: window.innerWidth < 768 ? 44 : 'auto',
                fontSize: window.innerWidth < 768 ? 14 : 12,
                fontWeight: 600
              }}
            >
              Edit
            </button>
            <button 
              type="button" 
              className={`btn btn-sm ${panelMode==='stage'?'btn-primary':'btn-ghost'}`} 
              onClick={()=>{ setPanelMode('stage'); setSelectedIndex(null) }}
              style={{
                flex: window.innerWidth < 768 ? 1 : 'none',
                minHeight: window.innerWidth < 768 ? 44 : 'auto',
                fontSize: window.innerWidth < 768 ? 14 : 12,
                fontWeight: 600
              }}
            >
              Stage Selection
            </button>
          </div>
          <div style={{ 
            display: window.innerWidth < 768 ? 'none' : 'flex', 
            gap: 8 
          }}>
            <button type="button" onClick={publishMap} className={`btn btn-sm ${isPublished ? 'btn-primary' : 'btn-ghost'}`} aria-pressed={isPublished}>Publish</button>
            <button type="button" onClick={unpublishMap} className={`btn btn-sm ${!isPublished ? 'btn-primary' : 'btn-ghost'}`} aria-pressed={!isPublished}>Unpublish</button>
          </div>
        </div>

        {panelMode === 'region' && (
          <>
            {selectedIndex == null && (
              <div style={{ color: '#777', fontSize: 14 }}>Select a region on the map to edit its details.</div>
            )}

            {selectedIndex != null && (
              <>
                {selectedRegion && selectedRegion.id && (
                  <div style={{ fontSize: 12, color: '#555' }}>Lot ID: {selectedRegion.id.slice(-6)}</div>
                )}
                <label style={{ fontSize: 12 }}>Stage</label>
                {selectedRegion && selectedRegion.name ? (
                  <input value={selectedRegion.name} disabled style={{ padding: 8, border: '1px solid #ddd', borderRadius: 4, background: '#f9fafb', color: '#555' }} />
                ) : (
                  stagesCatalog && stagesCatalog.length > 0 ? (
                    <select value={form.name} onChange={(e)=>handleRegionFieldChange('name', e.target.value)} style={{ padding: 8, border: '1px solid #ddd', borderRadius: 4 }}>
                      <option value="">Select a stage</option>
                      {stagesCatalog.map((s)=> {
                        const stageName = typeof s === 'string' ? s : (s && s.name ? s.name : 'Unknown')
                        return <option key={stageName} value={stageName}>Stage {stageName}</option>
                      })}
                    </select>
                  ) : (
                    <div className="muted" style={{ fontSize: 12 }}>No stages available. Create stages from Stage Selection.</div>
                  )
                )}

                {/* Warning message if lot is not assigned to a stage */}
                {(!form.name || !form.name.trim()) && (!selectedRegion?.name) && (
                  <div style={{ 
                    padding: 12, 
                    background: '#fef3c7', 
                    border: '1px solid #f59e0b', 
                    borderRadius: 8, 
                    marginTop: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>
                    <span style={{ fontSize: 18 }}>⚠️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>
                        Lot Not Assigned to Stage
                      </div>
                      <div style={{ fontSize: 12, color: '#78350f' }}>
                        Please assign this lot to a stage first before filling details. Go to "Stage Selection" tab to assign the lot to a stage.
                      </div>
                    </div>
                  </div>
                )}

                <label style={{ fontSize: 12 }}>Lot Number</label>
                <input 
                  value={form.lotNumber} 
                  onChange={(e) => handleRegionFieldChange('lotNumber', e.target.value)} 
                  disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                  style={{ 
                    padding: 8, 
                    border: formErrors.lotNumber ? '1px solid #dc2626' : '1px solid #ddd', 
                    borderRadius: 4,
                    background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                    cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'text'
                  }} 
                  placeholder="e.g., 1A, A1, B2, Lot3"
                />
                {formErrors.lotNumber && (
                  <div style={{ color: '#dc2626', fontSize: 11, marginTop: 4 }}>
                    {formErrors.lotNumber}
                  </div>
                )}

                <label style={{ fontSize: 12 }}>Lot Size</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input 
                    value={form.lotSizeValue || ''} 
                    onChange={(e) => handleRegionFieldChange('lotSizeValue', e.target.value)} 
                    disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                    style={{ 
                      flex: 1,
                      padding: 8, 
                      border: formErrors.lotSize ? '1px solid #dc2626' : '1px solid #ddd', 
                      borderRadius: 4,
                      background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                      cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'text'
                    }} 
                    placeholder="e.g., 500"
                    type="number"
                    step="0.01"
                    min="0"
                  />
                  <select 
                    value={form.lotSizeUnit || 'm²'} 
                    onChange={(e) => handleRegionFieldChange('lotSizeUnit', e.target.value)}
                    disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                    style={{ 
                      padding: 8, 
                      border: '1px solid #ddd', 
                      borderRadius: 4,
                      minWidth: 100,
                      background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                      cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="m²">m²</option>
                    <option value="sqm">sqm</option>
                    <option value="hectares">hectares</option>
                    <option value="acres">acres</option>
                    <option value="sq ft">sq ft</option>
                    <option value="square feet">square feet</option>
                  </select>
                </div>
                {formErrors.lotSize && (
                  <div style={{ color: '#dc2626', fontSize: 11, marginTop: 4 }}>
                    {formErrors.lotSize}
                  </div>
                )}

                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>Status</span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      const currentStatus = form.status || 'AVAILABLE'
                      setEditingStatusColor({ status: currentStatus, color: statusColors[currentStatus] || '#4CAF50' })
                    }}
                    style={{
                      padding: '4px 8px',
                      fontSize: 11,
                      color: '#6b7280'
                    }}
                    title="Edit status colors"
                  >
                    🎨 Customize Colors
                  </button>
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select 
                    value={form.status} 
                    onChange={(e) => handleRegionFieldChange('status', e.target.value)} 
                    disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                    style={{ 
                      flex: 1, 
                      padding: 8, 
                      border: '1px solid #ddd', 
                      borderRadius: 4,
                      background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                      cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'pointer'
                    }}
                  >
                  <option value="AVAILABLE">Available</option>
                  <option value="SOLD">Sold</option>
                  <option value="LOCKED">Locked</option>
                </select>
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setShowSalesGroupModal(true)}
                    style={{
                      padding: '8px 12px',
                      background: form.group ? '#0ea5e9' : '#f59e0b',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: 'nowrap'
                    }}
                    title={form.group ? `Current: ${form.group}` : 'Assign sales group'}
                  >
                    {form.group ? `Group: ${form.group}${form.subGroup ? ` → ${form.subGroup}` : ''}` : 'Assign Group'}
                  </button>
                </div>
                {form.group && (
                  <div style={{ 
                    marginTop: 4, 
                    fontSize: 11, 
                    color: '#0ea5e9',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}>
                    <span>Sales Group:</span>
                    <span style={{ fontWeight: 600 }}>{form.group}</span>
                    {form.subGroup && (
                      <>
                        <span>→</span>
                        <span style={{ fontWeight: 600 }}>{form.subGroup}</span>
                      </>
                    )}
                  </div>
                )}

                <label style={{ fontSize: 12 }}>Price</label>
                <input 
                  type="number" 
                  value={form.price} 
                  onChange={(e) => handleRegionFieldChange('price', e.target.value)} 
                  disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                  style={{ 
                    padding: 8, 
                    border: formErrors.price ? '1px solid #dc2626' : '1px solid #ddd', 
                    borderRadius: 4,
                    background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                    cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'text'
                  }} 
                  placeholder="e.g., 250000"
                />
                {formErrors.price && (
                  <div style={{ color: '#dc2626', fontSize: 11, marginTop: 4 }}>
                    {formErrors.price}
                  </div>
                )}

                <label style={{ fontSize: 12 }}>Notes</label>
                <textarea 
                  value={form.notes} 
                  onChange={(e) => handleRegionFieldChange('notes', e.target.value)} 
                  disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                  style={{ 
                    padding: 8, 
                    border: formErrors.notes ? '1px solid #dc2626' : '1px solid #ddd', 
                    borderRadius: 4, 
                    minHeight: 80,
                    background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                    cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'text'
                  }} 
                  placeholder="Additional notes about this lot..."
                />
                {formErrors.notes && (
                  <div style={{ color: '#dc2626', fontSize: 11, marginTop: 4 }}>
                    {formErrors.notes}
                  </div>
                )}

                <label style={{ fontSize: 12 }}>Frontage</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input 
                    value={form.frontageValue || ''} 
                    onChange={(e) => handleRegionFieldChange('frontageValue', e.target.value)} 
                    disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                    style={{ 
                      flex: 1,
                      padding: 8, 
                      border: formErrors.frontage ? '1px solid #dc2626' : '1px solid #ddd', 
                      borderRadius: 4,
                      background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                      cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'text'
                    }} 
                    placeholder="e.g., 20"
                    type="number"
                    step="0.01"
                    min="0"
                  />
                  <select 
                    value={form.frontageUnit || 'm'} 
                    onChange={(e) => handleRegionFieldChange('frontageUnit', e.target.value)}
                    disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                    style={{ 
                      padding: 8, 
                      border: '1px solid #ddd', 
                      borderRadius: 4,
                      minWidth: 80,
                      background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                      cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="m">m</option>
                    <option value="meters">meters</option>
                    <option value="ft">ft</option>
                    <option value="feet">feet</option>
                  </select>
                </div>
                {formErrors.frontage && (
                  <div style={{ color: '#dc2626', fontSize: 11, marginTop: 4 }}>
                    {formErrors.frontage}
                  </div>
                )}

                <label style={{ fontSize: 12 }}>Aspect</label>
                <select 
                  value={form.aspect} 
                  onChange={(e) => handleRegionFieldChange('aspect', e.target.value)} 
                  disabled={(!form.name || !form.name.trim()) && (!selectedRegion?.name)}
                  style={{ 
                    padding: 8, 
                    border: '1px solid #ddd', 
                    borderRadius: 4,
                    background: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? '#f3f4f6' : 'white',
                    cursor: ((!form.name || !form.name.trim()) && (!selectedRegion?.name)) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <option value="">Select Aspect</option>
                  <option value="North">North</option>
                  <option value="North East">North East</option>
                  <option value="East">East</option>
                  <option value="South East">South East</option>
                  <option value="South">South</option>
                  <option value="South West">South West</option>
                  <option value="West">West</option>
                  <option value="North West">North West</option>
                </select>

                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={saveSelectedRegion} style={{ padding: '8px 12px', background: '#007bff', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Save</button>
                </div>
              </>
            )}
          </>
        )}

        {panelMode === 'stage' && (
          <div style={{ display: 'grid', gap: 16 }}>
            {/* Tab Navigation */}
            <div className="stage-tabs" style={{ 
              display: window.innerWidth < 768 ? 'grid' : 'flex', 
              gridTemplateColumns: window.innerWidth < 768 ? '1fr 1fr 1fr' : 'none',
              gap: window.innerWidth < 768 ? 4 : 8, 
              borderBottom: window.innerWidth < 768 ? 'none' : '2px solid #e5e7eb', 
              paddingBottom: window.innerWidth < 768 ? 0 : 8,
              background: window.innerWidth < 768 ? '#f1f5f9' : 'transparent',
              padding: window.innerWidth < 768 ? 4 : 0,
              borderRadius: window.innerWidth < 768 ? 8 : 0,
              marginBottom: window.innerWidth < 768 ? 12 : 0
            }}>
              <button 
                className={`btn btn-sm stage-tab ${stageActionMode === 'create' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => { setStageActionMode('create'); setStageEditTarget(null); setStageViewTarget(null) }}
                style={{ 
                  flex: window.innerWidth < 768 ? 'none' : 1,
                  display: 'flex',
                  flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: window.innerWidth < 768 ? 4 : 6,
                  minHeight: window.innerWidth < 768 ? 60 : 'auto',
                  padding: window.innerWidth < 768 ? '12px 8px' : '8px 12px',
                  fontSize: window.innerWidth < 768 ? 12 : 14,
                  fontWeight: 600
                }}
              >
                <FontAwesomeIcon icon={faPlus} style={{ fontSize: window.innerWidth < 768 ? 16 : 14 }} />
                <span>Create</span>
              </button>
              <button 
                className={`btn btn-sm stage-tab ${stageActionMode === 'assign' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => { setStageActionMode('assign'); setStageEditTarget(null); setStageViewTarget(null) }}
                style={{ 
                  flex: window.innerWidth < 768 ? 'none' : 1,
                  display: 'flex',
                  flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: window.innerWidth < 768 ? 4 : 6,
                  minHeight: window.innerWidth < 768 ? 60 : 'auto',
                  padding: window.innerWidth < 768 ? '12px 8px' : '8px 12px',
                  fontSize: window.innerWidth < 768 ? 12 : 14,
                  fontWeight: 600
                }}
                disabled={!stagesCatalog || stagesCatalog.length === 0}
              >
                <img src="/icons/edit.png" alt="Assign" style={{ width: window.innerWidth < 768 ? 20 : 16, height: window.innerWidth < 768 ? 20 : 16 }} />
                <span>Assign</span>
              </button>
              <button 
                className={`btn btn-sm stage-tab ${stageActionMode === 'view' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => { setStageActionMode('view'); setStageEditTarget(null); setStageViewTarget(null) }}
                style={{ 
                  flex: window.innerWidth < 768 ? 'none' : 1,
                  display: 'flex',
                  flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: window.innerWidth < 768 ? 4 : 6,
                  minHeight: window.innerWidth < 768 ? 60 : 'auto',
                  padding: window.innerWidth < 768 ? '12px 8px' : '8px 12px',
                  fontSize: window.innerWidth < 768 ? 12 : 14,
                  fontWeight: 600
                }}
                disabled={!stagesCatalog || stagesCatalog.length === 0}
              >
                <img src="/icons/view.png" alt="View" style={{ width: window.innerWidth < 768 ? 20 : 16, height: window.innerWidth < 768 ? 20 : 16 }} />
                <span>View</span>
              </button>
            </div>

            {/* Create Section */}
            {stageActionMode === 'create' && (
              <div style={{ display: 'grid', gap: 16 }}>
                {/* Create Stages Section */}
                <div className="stage-section" style={{ padding: 20, background: '#f8fafc', borderRadius: 12, border: '2px solid #e2e8f0' }}>
                  <div className="stage-section-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div className="stage-section-icon" style={{ 
                      width: 32, 
                      height: 32, 
                      minWidth: 32,
                      minHeight: 32,
                      borderRadius: '50%', 
                      background: '#3b82f6', 
                      color: 'white', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      fontWeight: 'bold',
                      fontSize: 14,
                      flexShrink: 0
                    }}>
                      <img src="/icons/plus.svg" alt="Create" style={{ width: 16, height: 16 }} />
                    </div>
                    <div>
                      <h3 className="stage-section-title" style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#1f2937' }}>Stages</h3>
                      <p className="stage-section-subtitle" style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>Create stages to organize your lots</p>
                    </div>
                  </div>
                  
                  {(stagesCatalog && stagesCatalog.length > 0) && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Existing Stages</div>
                      <div className="stage-list" style={{ display: 'grid', gap: 8 }}>
                        {stagesCatalog.map((s) => {
                          const stageName = typeof s === 'string' ? s : (s && s.name ? s.name : 'Unknown')
                          const isLocked = typeof s === 'object' && s ? (s.isLocked || false) : false
                          const regionCount = getStageRegionCounts()[stageName]
                          const canDelete = regionCount === 0
                          const isLoading = stageLockLoading[stageName]
                          return (
                            <div key={stageName} className="stage-item" style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 12, 
                              padding: 12, 
                              background: isLocked ? '#fef2f2' : '#fff',
                              border: isLocked ? '1px solid #fecaca' : '1px solid #e5e7eb',
                              borderRadius: 8,
                              opacity: isLocked ? 0.8 : 1
                            }}>
                              <div className="stage-color-indicator" style={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: 6, 
                                background: isLocked ? '#9ca3af' : getStageColor(stageName),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: 12
                              }}>{stageName}</div>
                              <div className="stage-item-content" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  <div style={{ fontWeight: 500, color: isLocked ? '#9ca3af' : 'inherit' }}>
                                    Stage {stageName} {isLocked && '(Locked)'}
                                  </div>
                                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                                    {regionCount} lot{regionCount !== 1 ? 's' : ''}
                                  </div>
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                <button 
                                  className="btn btn-ghost btn-sm" 
                                  onClick={() => handleLockStageClick(stageName, isLocked)}
                                  disabled={isLoading}
                                  style={{ 
                                    color: isLocked ? '#059669' : '#6b7280', 
                                    fontSize: 11,
                                    padding: '4px 8px',
                                    opacity: isLoading ? 0.5 : 1,
                                    cursor: isLoading ? 'not-allowed' : 'pointer'
                                  }}
                                  title={isLocked ? 'Unlock stage' : 'Lock stage'}
                                >
                                  {isLoading ? '...' : (isLocked ? '🔓' : '🔒')}
                                </button>
                                <button 
                                  className="btn btn-ghost btn-sm" 
                                  onClick={() => deleteStage(stageName)}
                                  disabled={!canDelete || isLocked}
                                  style={{ 
                                    color: canDelete && !isLocked ? '#ef4444' : '#9ca3af', 
                                    fontSize: 11,
                                    padding: '4px 8px',
                                    opacity: (canDelete && !isLocked) ? 1 : 0.5,
                                    cursor: (canDelete && !isLocked) ? 'pointer' : 'not-allowed'
                                  }}
                                >
                                  {canDelete && !isLocked ? 'Delete' : 'Locked'}
                                </button>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                  
                  <button 
                    className="btn btn-primary" 
                    onClick={() => { setStageEditTarget(null); setStageNameInput(''); createStage() }}
                    disabled={!canCreateNewStage()}
                    style={{ 
                      width: '100%', 
                      padding: '12px 16px', 
                      fontSize: 14, 
                      fontWeight: 600,
                      opacity: canCreateNewStage() ? 1 : 0.5,
                      cursor: canCreateNewStage() ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8
                    }}
                  >
                    <img src="/icons/plus.svg" alt="Create" style={{ width: 16, height: 16 }} />
                    {canCreateNewStage() ? 'Create New Stage' : 'Complete Previous Stage First'}
                  </button>
                </div>

              </div>
            )}

            {/* Assign Section */}
            {stageActionMode === 'assign' && stagesCatalog && stagesCatalog.length > 0 && (
              <div style={{ display: 'grid', gap: 16 }}>
                {!stageEditTarget ? (
                  <>
                    {/* Assign to Stages Section */}
                    <div className="stage-section" style={{ padding: 20, background: '#fef3c7', borderRadius: 12, border: '2px solid #f59e0b' }}>
                      <div className="stage-section-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                        <div className="stage-section-icon" style={{ 
                          width: 32, 
                          height: 32, 
                          minWidth: 32,
                          minHeight: 32,
                          borderRadius: '50%', 
                          background: '#f59e0b', 
                          color: 'white', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          fontWeight: 'bold',
                          fontSize: 14,
                          flexShrink: 0
                        }}>
                          <img src="/icons/layers.svg" alt="Assign" style={{ width: 16, height: 16 }} />
                        </div>
                        <div>
                          <h3 className="stage-section-title" style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#92400e' }}>Step 1: Assign to Stages</h3>
                          <p className="stage-section-subtitle" style={{ margin: 0, fontSize: 14, color: '#a16207' }}>Click lots on the map, then assign them to a stage</p>
                        </div>
                      </div>

                      <div style={{ 
                        fontSize: 12, 
                        color: '#a16207', 
                        marginBottom: 12,
                        padding: 10,
                        background: '#fef3c7',
                        borderRadius: 6,
                        border: '1px solid #fcd34d'
                      }}>
                        <strong>📌 Step 1:</strong> First, assign lots to a stage. Click lots on the map, then select a stage below.
                      </div>

                      <div className="stage-list" style={{ display: 'grid', gap: 10, marginBottom: 16 }}>
                        {stagesCatalog.map((s) => {
                          const stageName = typeof s === 'string' ? s : (s && s.name ? s.name : 'Unknown')
                          const isLocked = typeof s === 'object' && s ? (s.isLocked || false) : false
                          const stageRegionCount = getStageRegionCounts()[stageName] || 0
                          
                          return (
                            <button 
                              key={stageName}
                              className="btn stage-assign-button"
                              onClick={() => {
                                setStageEditTarget(stageName)
                                setStageNameInput(stageName)
                                clearStageSelection()
                              }}
                              disabled={isLocked}
                              style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 12, 
                                padding: '14px 16px',
                                background: isLocked ? '#f9fafb' : '#fff',
                                border: isLocked ? '1px solid #d1d5db' : '2px solid #f59e0b',
                                borderRadius: 8,
                                textAlign: 'left',
                                width: '100%',
                                opacity: isLocked ? 0.6 : 1,
                                cursor: isLocked ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (!isLocked) {
                                  e.currentTarget.style.background = '#fef3c7'
                                  e.currentTarget.style.borderColor = '#f59e0b'
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isLocked) {
                                  e.currentTarget.style.background = '#fff'
                                  e.currentTarget.style.borderColor = '#f59e0b'
                                }
                              }}
                            >
                              <div style={{ 
                                width: 28, 
                                height: 28, 
                                borderRadius: 6, 
                                background: isLocked ? '#9ca3af' : getStageColor(stageName),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: 14,
                                flexShrink: 0
                              }}>{stageName}</div>
                              <div style={{ flex: 1, textAlign: 'left' }}>
                                <div style={{ fontWeight: 600, fontSize: 15, color: isLocked ? '#9ca3af' : 'inherit', marginBottom: 4 }}>
                                  Stage {stageName} {isLocked && '(Locked)'}
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                  {stageRegionCount} lot{stageRegionCount !== 1 ? 's' : ''} assigned
                                </div>
                              </div>
                            </button>
                          )
                        })}
                      </div>
                    </div>

                  </>
                ) : (
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 12, 
                      marginBottom: 16,
                      padding: 14,
                      background: '#fff',
                      borderRadius: 8,
                      border: '2px solid #f59e0b'
                    }}>
                          <div style={{ 
                            width: 28, 
                            height: 28, 
                            borderRadius: 6, 
                            background: getStageColor(stageEditTarget),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: 14,
                            flexShrink: 0
                          }}>{stageEditTarget}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: 2 }}>
                              Stage {stageEditTarget}
                            </div>
                            <div style={{ fontSize: 11, color: '#a16207' }}>
                              Assign lots to this stage
                            </div>
                          </div>
                      <button 
                        className="btn btn-ghost btn-sm" 
                        onClick={() => { setStageEditTarget(null) }}
                        style={{ 
                          marginLeft: 'auto',
                          padding: '6px 12px',
                          border: '1px solid #d1d5db'
                        }}
                      >
                        Cancel
                      </button>
                    </div>

                          <div className="stage-instructions" style={{ 
                            padding: 16, 
                            background: '#fff', 
                            borderRadius: 8, 
                            border: '1px solid #e5e7eb',
                            marginBottom: 16
                          }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                              📍 Stage Assignment
                            </div>
                            <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, marginBottom: 12 }}>
                                  <div style={{ marginBottom: 4 }}>1. Click on lots in the map to select them</div>
                                  <div style={{ marginBottom: 4 }}>2. Selected lots will turn blue</div>
                                  <div style={{ marginBottom: 4 }}>3. Lots already in this stage will be removed when selected</div>
                                  <div>4. Click "Assign to Stage" when done</div>
                            </div>
                      
                            <div className="stage-selection-info" style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              justifyContent: 'space-between',
                              padding: 14,
                              background: selectedRegionIdsForStage.size > 0 ? '#fef3c7' : '#f3f4f6',
                              borderRadius: 8,
                              border: selectedRegionIdsForStage.size > 0 ? '2px solid #f59e0b' : '1px solid #d1d5db'
                            }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: selectedRegionIdsForStage.size > 0 ? '#92400e' : '#6b7280', marginBottom: 4 }}>
                            Selected lots: <strong style={{ color: selectedRegionIdsForStage.size > 0 ? '#b45309' : '#6b7280' }}>
                              {selectedRegionIdsForStage.size}
                            </strong>
                          </div>
                        </div>
                        {selectedRegionIdsForStage.size > 0 && (
                          <button 
                            className="btn btn-ghost btn-sm" 
                            onClick={() => clearStageSelection()}
                            style={{ 
                              padding: '6px 12px',
                              border: '1px solid #d1d5db'
                            }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                          <button 
                            className="btn btn-primary stage-assign-button-main" 
                            onClick={() => assignStageToSelected(stageEditTarget, null)}
                            disabled={selectedRegionIdsForStage.size === 0}
                            style={{ 
                              width: '100%', 
                              padding: '12px 16px', 
                              fontSize: 14, 
                              fontWeight: 600,
                              opacity: selectedRegionIdsForStage.size === 0 ? 0.5 : 1,
                              cursor: selectedRegionIdsForStage.size === 0 ? 'not-allowed' : 'pointer'
                            }}
                          >
                      {selectedRegionIdsForStage.size === 0 
                        ? 'Select lots on the map first' 
                        : (() => {
                            const target = `Stage ${stageEditTarget}`
                            const alreadyInTarget = Array.from(selectedRegionIdsForStage).filter(id => {
                                return isRegionInCurrentStage(id)
                            }).length
                            const newToTarget = selectedRegionIdsForStage.size - alreadyInTarget
                            if (alreadyInTarget > 0 && newToTarget > 0) {
                              return `✅ Add ${newToTarget} lot${newToTarget !== 1 ? 's' : ''} & Remove ${alreadyInTarget} lot${alreadyInTarget !== 1 ? 's' : ''} from ${target}`
                            } else if (alreadyInTarget > 0) {
                              return `✅ Remove ${alreadyInTarget} lot${alreadyInTarget !== 1 ? 's' : ''} from ${target}`
                            } else {
                              return `✅ Add ${newToTarget} lot${newToTarget !== 1 ? 's' : ''} to ${target}`
                            }
                          })()
                      }
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* View Section */}
            {stageActionMode === 'view' && stagesCatalog && stagesCatalog.length > 0 && (
              <div className="stage-section" style={{ padding: 20, background: '#f0f9ff', borderRadius: 12, border: '2px solid #0ea5e9' }}>
                <div className="stage-section-header" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <div className="stage-section-icon" style={{ 
                  width: 32, 
                  height: 32, 
                  minWidth: 32,
                  minHeight: 32,
                  borderRadius: '50%', 
                  background: '#0ea5e9', 
                  color: 'white', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  fontWeight: 'bold',
                  fontSize: 14,
                  flexShrink: 0
                }}>
                    <img src="/icons/view.png" alt="View" style={{ width: 16, height: 16 }} />
                  </div>
                  <div>
                    <h3 className="stage-section-title" style={{ margin: 0, fontSize: 18, fontWeight: 600, color: '#0c4a6e' }}>View Your Stages</h3>
                    <p className="stage-section-subtitle" style={{ margin: 0, fontSize: 14, color: '#0369a1' }}>See which lots belong to each stage</p>
                  </div>
                </div>

                <div className="stage-list" style={{ display: 'grid', gap: 8 }}>
                  {stagesCatalog.map((s) => {
                    const stageName = typeof s === 'string' ? s : (s && s.name ? s.name : 'Unknown')
                    const isLocked = typeof s === 'object' && s ? (s.isLocked || false) : false
                    return (
                      <button 
                        key={stageName}
                        className={`btn stage-assign-button ${stageViewTarget === stageName ? 'btn-primary' : ''}`}
                        onClick={() => {
                          setStageViewTarget(stageViewTarget === stageName ? null : stageName)
                          setStageEditTarget(null)
                        }}
                        disabled={isLocked}
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 12, 
                          padding: '12px 16px',
                          background: isLocked ? '#f9fafb' : (stageViewTarget === stageName ? '#3b82f6' : '#fff'),
                          color: isLocked ? '#9ca3af' : (stageViewTarget === stageName ? '#fff' : '#374151'),
                          border: isLocked ? '1px solid #d1d5db' : (stageViewTarget === stageName ? '1px solid #3b82f6' : '1px solid #e5e7eb'),
                          borderRadius: 8,
                          textAlign: 'left',
                          width: '100%',
                          opacity: isLocked ? 0.6 : 1,
                          cursor: isLocked ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <div className="stage-color-indicator" style={{ 
                          width: 20, 
                          height: 20, 
                          borderRadius: 4, 
                          background: isLocked ? '#9ca3af' : (stageViewTarget === stageName ? '#fff' : getStageColor(stageName)),
                          color: isLocked ? '#fff' : (stageViewTarget === stageName ? getStageColor(stageName) : '#fff'),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: 12
                        }}>{stageName}</div>
                        <span style={{ fontWeight: 500 }}>
                          {stageViewTarget === stageName ? '👁️ Currently viewing Stage ' : 'View Stage '}{stageName} {isLocked && '(Locked)'}
                        </span>
                        <span style={{ fontSize: 12, marginLeft: 'auto', opacity: 0.7 }}>
                          {getStageRegionCounts()[stageName]} lot{getStageRegionCounts()[stageName] !== 1 ? 's' : ''}
                        </span>
                      </button>
                    )
                  })}
                </div>

                {stageViewTarget && (
                  <div style={{ 
                    marginTop: 16, 
                    padding: 12, 
                    background: '#dbeafe', 
                    borderRadius: 8, 
                    border: '1px solid #3b82f6' 
                  }}>
                    <div style={{ fontSize: 13, color: '#1e40af', fontWeight: 500 }}>
                      👁️ Only showing lots in Stage {stageViewTarget}
                    </div>
                    <button 
                      className="btn btn-ghost btn-sm" 
                      onClick={() => setStageViewTarget(null)}
                      style={{ marginTop: 8 }}
                    >
                      Show All Lots
                    </button>
                  </div>
                )}
              </div>
            )}

          </div>
        )}

      </div>

      {/* Display Label Selection Modal */}
      {showDisplayLabelModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => {
          setShowDisplayLabelModal(null)
          setDisplayLabelInput('')
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 500,
            width: '90%',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 600, color: '#1f2937' }}>
              Lock Stage {showDisplayLabelModal}
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: 14, color: '#6b7280' }}>
              Choose how this stage should be displayed in the viewer:
            </p>
            
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                Display Label (optional)
              </label>
              <select
                value={displayLabelInput}
                onChange={(e) => setDisplayLabelInput(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                  marginBottom: 8
                }}
              >
                <option value="">Default: "Next Release"</option>
                <option value="Next Release">Next Release</option>
                <option value="Sold">Sold</option>
                <option value="Coming Soon">Coming Soon</option>
                <option value="Pre-Release">Pre-Release</option>
                <option value="Sold Out">Sold Out</option>
                <option value="Reserved">Reserved</option>
                <option value="On Hold">On Hold</option>
              </select>
              <input
                type="text"
                value={displayLabelInput}
                onChange={(e) => setDisplayLabelInput(e.target.value)}
                placeholder="Or enter custom label..."
                maxLength={50}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14
                }}
              />
              <p style={{ margin: '8px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                If left empty, will display "Next Release" by default. Max 50 characters.
              </p>
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowDisplayLabelModal(null)
                  setDisplayLabelInput('')
                }}
                style={{ padding: '10px 20px' }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleConfirmDisplayLabel(showDisplayLabelModal)}
                disabled={stageLockLoading[showDisplayLabelModal]}
                style={{ padding: '10px 20px' }}
              >
                {stageLockLoading[showDisplayLabelModal] ? 'Locking...' : 'Lock Stage'}
              </button>
            </div>
          </div>
        </div>
        )}
      
      {/* Sales Group Selection Modal */}
      {showSalesGroupModal && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={handleSalesGroupCancel}>
          <div className="modal" style={{ maxWidth: '500px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Select Sales Group</div>
              <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={handleSalesGroupCancel}>✕</button>
      </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ marginBottom: 16, fontSize: 14, color: '#6b7280' }}>
                {form.group ? (
                  <div>
                    <div style={{ marginBottom: 8 }}>
                      Current sales group: <strong>{form.group}</strong>
                      {form.subGroup && <span> → <strong>{form.subGroup}</strong></span>}
                    </div>
                    <div>Select a different sales group/sub-group or create a new one:</div>
                  </div>
                ) : (
                  <div>Which sales group (and optionally sub-group) is this lot in?</div>
                )}
              </div>
              
              {/* Current Group/Sub-Group Display */}
              {form.group && (
                <div style={{ 
                  marginBottom: 16, 
                  padding: 12, 
                  background: '#f0f9ff', 
                  borderRadius: 8, 
                  border: '2px solid #0ea5e9',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8
                }}>
                  {(() => {
                    const currentGroup = getSalesGroups.find(g => g.name === form.group)
                    const groupColor = getGroupColor(form.group)
                    const subGroupColor = form.subGroup ? getGroupColor(form.group, form.subGroup) : null
                    return (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            background: groupColor,
                            flexShrink: 0
                          }}
                        />
                        <div style={{ flex: 1, fontWeight: 600, fontSize: 14, color: '#0c4a6e' }}>
                          {form.group}
                            {form.subGroup && (
                              <span style={{ marginLeft: 8, fontSize: 12, color: '#0369a1', fontWeight: 500 }}>
                                → {form.subGroup}
                              </span>
                            )}
                        </div>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setRenamingGroup({ oldName: form.group, newName: form.group })
                          }}
                          style={{ 
                            padding: '4px 8px', 
                            fontSize: 12,
                            color: '#6b7280'
                          }}
                          title="Rename group"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => {
                            setEditingGroupColor({ groupName: form.group, color: groupColor })
                          }}
                          style={{ 
                            padding: '4px 8px', 
                            fontSize: 12,
                            color: '#6b7280'
                          }}
                          title="Change color"
                        >
                          🎨
                        </button>
                        </div>
                        {form.subGroup && subGroupColor && (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 12, 
                            paddingLeft: 36,
                            fontSize: 13,
                            color: '#0369a1'
                          }}>
                            <div
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                background: subGroupColor,
                                flexShrink: 0
                              }}
                            />
                            <span style={{ fontWeight: 500 }}>Sub-group: {form.subGroup}</span>
                          </div>
                        )}
                      </>
                    )
                  })()}
                </div>
              )}
              
              <div style={{ display: 'grid', gap: 6, maxHeight: '400px', overflowY: 'auto', marginBottom: 12 }}>
                {getSalesGroups.map((group) => {
                  // Auto-expand if this group has a selected sub-group
                  const shouldBeExpanded = form.group === group.name && form.subGroup
                  const isExpanded = expandedGroups.has(group.name) || shouldBeExpanded
                  const hasSubGroups = group.subGroups && group.subGroups.length > 0
                  const isGroupSelected = form.group === group.name && !form.subGroup
                  const isSubGroupSelected = form.group === group.name && form.subGroup
                  
                  return (
                    <div key={group.name} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {/* Group Row */}
                      <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                          gap: 8,
                          padding: '10px 12px',
                          background: isGroupSelected ? '#f0f9ff' : '#fff',
                          border: isGroupSelected ? '2px solid #0ea5e9' : '1px solid #e5e7eb',
                          borderRadius: 6,
                      transition: 'all 0.2s'
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        background: group.color,
                        flexShrink: 0
                      }}
                    />
                    {renamingGroup?.oldName === group.name ? (
                      <div style={{ flex: 1, display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          type="text"
                          value={renamingGroup.newName}
                          onChange={(e) => setRenamingGroup({ ...renamingGroup, newName: e.target.value })}
                          onBlur={(e) => {
                            // Only rename on blur if the value actually changed
                            const newValue = e.target.value.trim()
                            if (newValue && newValue !== renamingGroup.oldName) {
                              handleRenameGroup(renamingGroup.oldName, newValue)
                            } else {
                              setRenamingGroup(null)
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              e.stopPropagation()
                              const newValue = renamingGroup.newName.trim()
                              if (newValue && newValue !== renamingGroup.oldName) {
                                handleRenameGroup(renamingGroup.oldName, newValue)
                              } else {
                                setRenamingGroup(null)
                              }
                            } else if (e.key === 'Escape') {
                              e.preventDefault()
                              e.stopPropagation()
                              setRenamingGroup(null)
                            }
                          }}
                          autoFocus
                          style={{
                            flex: 1,
                            padding: '6px 10px',
                            border: '1px solid #c084fc',
                            borderRadius: 4,
                            fontSize: 14,
                            fontWeight: 600
                          }}
                        />
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            const newValue = renamingGroup.newName.trim()
                            if (newValue && newValue !== renamingGroup.oldName) {
                              handleRenameGroup(renamingGroup.oldName, newValue)
                            } else {
                              setRenamingGroup(null)
                            }
                          }}
                          style={{ padding: '4px 8px', fontSize: 12 }}
                          title="Save"
                        >
                          ✓
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setRenamingGroup(null)
                          }}
                          style={{ padding: '4px 8px', fontSize: 12 }}
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        {hasSubGroups && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleGroupExpansion(group.name)
                            }}
                            style={{ 
                              padding: '2px 6px', 
                              fontSize: 12,
                              color: '#6b7280',
                              minWidth: 20,
                              background: isExpanded ? '#e5e7eb' : 'transparent',
                              borderRadius: 4
                            }}
                            title={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? '▼' : '▶'}
                          </button>
                        )}
                        <div 
                          style={{ flex: 1, fontWeight: 600, fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
                          onClick={() => handleSalesGroupSelect(group.name)}
                        >
                          <span>{group.name}</span>
                          {isGroupSelected && (
                            <span style={{ fontSize: 11, color: '#0ea5e9', fontWeight: 500 }}>✓</span>
                          )}
                        </div>
                        {form.group !== group.name && (
                          <>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                setRenamingGroup({ oldName: group.name, newName: group.name })
                              }}
                              style={{ 
                                padding: '4px 8px', 
                                fontSize: 12,
                                color: '#6b7280'
                              }}
                              title="Rename group"
                            >
                              ✏️
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={(e) => {
                                e.stopPropagation()
                                setEditingGroupColor({ groupName: group.name, color: group.color })
                              }}
                              style={{ 
                                padding: '4px 8px', 
                                fontSize: 12,
                                color: '#6b7280'
                              }}
                              title="Change color"
                            >
                              🎨
                            </button>
                          </>
                        )}
                        {/* Show Add Sub button for all groups, even when selected */}
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowCreateSubGroupModal(group.name)
                            setNewSubGroupName('')
                            // Auto-expand the group when adding a sub-group
                            if (!expandedGroups.has(group.name)) {
                              toggleGroupExpansion(group.name)
                            }
                          }}
                          style={{
                            padding: '4px 8px', 
                            fontSize: 12,
                            color: '#6b7280'
                          }}
                          title="Add sub-group"
                        >
                          + Sub
                        </button>
                        {!isGroupSelected && (
                        <button
                            className="btn btn-sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSalesGroupSelect(group.name)
                            }}
                          style={{
                              padding: '4px 10px',
                            background: group.color,
                            color: '#fff',
                            border: 'none',
                              borderRadius: 4,
                              fontSize: 11,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                            Select
                        </button>
                        )}
                      </>
                    )}
                  </div>
                      
                      {/* Sub-Groups List */}
                      {hasSubGroups && isExpanded && (
                        <div style={{ 
                          marginLeft: 28, 
                          marginTop: 2, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          gap: 3,
                          paddingLeft: 8,
                          borderLeft: '2px solid #e5e7eb'
                        }}>
                          {group.subGroups.map((subGroup) => {
                            const isSubGroupSelected = form.group === group.name && form.subGroup === subGroup.name
                            const isRenaming = renamingSubGroup && renamingSubGroup.groupName === group.name && renamingSubGroup.oldName === subGroup.name
                            
                            if (isRenaming) {
                              return (
                                <div
                                  key={subGroup.name}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '6px 10px',
                                    background: '#fff7ed',
                                    border: '1px solid #fb923c',
                                    borderRadius: 4
                                  }}
                                >
                                  <input
                                    type="text"
                                    value={renamingSubGroup.newName}
                                    onChange={(e) => setRenamingSubGroup({ ...renamingSubGroup, newName: e.target.value })}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleRenameSubGroup(group.name, subGroup.name, renamingSubGroup.newName)
                                      } else if (e.key === 'Escape') {
                                        setRenamingSubGroup(null)
                                      }
                                    }}
                                    autoFocus
                                    style={{
                                      flex: 1,
                                      padding: '4px 8px',
                                      border: '1px solid #fb923c',
                                      borderRadius: 4,
                                      fontSize: 12
                                    }}
                                  />
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleRenameSubGroup(group.name, subGroup.name, renamingSubGroup.newName)}
                                    style={{ padding: '4px 8px', fontSize: 11 }}
                                  >
                                    ✓
                                  </button>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setRenamingSubGroup(null)}
                                    style={{ padding: '4px 8px', fontSize: 11 }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              )
                            }
                            
                            return (
                              <div
                                key={subGroup.name}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  padding: '6px 10px',
                                  background: isSubGroupSelected ? '#f0f9ff' : '#f9fafb',
                                  border: isSubGroupSelected ? '2px solid #0ea5e9' : '1px solid #e5e7eb',
                                  borderRadius: 4,
                                  transition: 'all 0.2s',
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleSalesGroupSelect(group.name, subGroup.name)}
                              >
                                <div
                                  style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: 3,
                                    background: subGroup.color,
                                    flexShrink: 0,
                                    cursor: 'pointer',
                                    border: '1px solid rgba(0,0,0,0.1)'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setEditingGroupColor({ groupName: group.name, subGroupName: subGroup.name, color: subGroup.color })
                                  }}
                                  title="Change color"
                                />
                                <div 
                                  style={{ flex: 1, fontSize: 12, fontWeight: isSubGroupSelected ? 600 : 500, color: isSubGroupSelected ? '#0c4a6e' : '#374151' }}
                                >
                                  {subGroup.name}
                                </div>
                                {isSubGroupSelected && (
                                  <span style={{ fontSize: 12, color: '#0ea5e9', fontWeight: 600 }}>✓</span>
                                )}
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setEditingGroupColor({ groupName: group.name, subGroupName: subGroup.name, color: subGroup.color })
                                  }}
                                  style={{ 
                                    padding: '2px 6px', 
                                    fontSize: 10,
                                    minWidth: 'auto',
                                    height: 'auto'
                                  }}
                                  title="Change color"
                                >
                                  🎨
                                </button>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setRenamingSubGroup({ groupName: group.name, oldName: subGroup.name, newName: subGroup.name })
                                  }}
                                  style={{ 
                                    padding: '2px 6px', 
                                    fontSize: 10,
                                    minWidth: 'auto',
                                    height: 'auto'
                                  }}
                                  title="Rename"
                                >
                                  ✏️
                                </button>
                              </div>
                            )
                          })}
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              setShowCreateSubGroupModal(group.name)
                              setNewSubGroupName('')
                            }}
                            style={{
                              padding: '6px 12px',
                              border: '1px dashed #c084fc',
                              borderRadius: 6,
                              color: '#7c3aed',
                              fontSize: 11,
                              fontWeight: 500,
                              marginTop: 4
                            }}
                          >
                            + Add Sub-Group
                          </button>
                        </div>
                      )}
                      
                      {/* Create Sub-Group Input */}
                      {showCreateSubGroupModal === group.name && (
                        <div style={{ 
                          marginLeft: 24, 
                          marginTop: 4,
                          padding: 12,
                          background: '#faf5ff',
                          borderRadius: 8,
                          border: '1px solid #e9d5ff',
                          paddingLeft: 36
                        }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b21a8', marginBottom: 8 }}>
                            New Sub-Group Name:
                          </div>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <input
                              type="text"
                              value={newSubGroupName}
                              onChange={(e) => setNewSubGroupName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleCreateNewSubGroup(group.name)
                                } else if (e.key === 'Escape') {
                                  setShowCreateSubGroupModal(null)
                                  setNewSubGroupName('')
                                }
                              }}
                              placeholder="Enter sub-group name..."
                              autoFocus
                              style={{
                                flex: 1,
                                padding: '6px 10px',
                                border: '1px solid #c084fc',
                                borderRadius: 4,
                                fontSize: 12
                              }}
                            />
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleCreateNewSubGroup(group.name)}
                              style={{ padding: '6px 12px', fontSize: 11 }}
                            >
                              Create
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              onClick={() => {
                                setShowCreateSubGroupModal(null)
                                setNewSubGroupName('')
                              }}
                              style={{ padding: '6px 12px', fontSize: 11 }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
              
              {!showCreateGroupModal ? (
                <button
                  className="btn btn-ghost"
                  onClick={() => setShowCreateGroupModal(true)}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    border: '2px dashed #c084fc',
                    borderRadius: 8,
                    color: '#7c3aed',
                    fontWeight: 600,
                    marginBottom: 12
                  }}
                >
                  + Create New Sales Group
                </button>
              ) : (
                <div style={{ 
                  padding: 12, 
                  background: '#faf5ff', 
                  borderRadius: 8, 
                  border: '1px solid #e9d5ff',
                  marginBottom: 12
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#6b21a8', marginBottom: 8 }}>
                    New Sales Group Name:
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="text"
                      value={newGroupName}
                      onChange={(e) => setNewGroupName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCreateNewGroup()
                        } else if (e.key === 'Escape') {
                          setShowCreateGroupModal(false)
                          setNewGroupName('')
                        }
                      }}
                      placeholder="Enter group name..."
                      autoFocus
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        border: '1px solid #c084fc',
                        borderRadius: 6,
                        fontSize: 14
                      }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleCreateNewGroup}
                      style={{ padding: '8px 16px' }}
                    >
                      Create
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={() => {
                        setShowCreateGroupModal(false)
                        setNewGroupName('')
                      }}
                      style={{ padding: '8px 16px' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn btn-ghost" onClick={handleSalesGroupCancel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Color Picker Modal */}
      {editingGroupColor && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setEditingGroupColor(null)}>
          <div className="modal" style={{ maxWidth: '400px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                Change Color for {editingGroupColor.subGroupName 
                  ? `${editingGroupColor.groupName} > ${editingGroupColor.subGroupName}` 
                  : editingGroupColor.groupName}
              </div>
              <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setEditingGroupColor(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ marginBottom: 16, fontSize: 14, color: '#6b7280' }}>
                Select a color for this {editingGroupColor.subGroupName ? 'sub-group' : 'sales group'}:
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                {groupColorPalette.map((color) => (
                  <button
                    key={color}
                    className="btn"
                    onClick={() => handleChangeGroupColor(editingGroupColor.groupName, color, editingGroupColor.subGroupName || null)}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      background: color,
                      border: editingGroupColor.color === color ? '3px solid #000' : '2px solid #e5e7eb',
                      borderRadius: 8,
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    title={color}
                  >
                    {editingGroupColor.color === color && (
                      <span style={{ 
                        position: 'absolute', 
                        top: '50%', 
                        left: '50%', 
                        transform: 'translate(-50%, -50%)',
                        fontSize: 20,
                        color: '#fff',
                        textShadow: '0 0 4px rgba(0,0,0,0.5)'
                      }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                  Or enter a custom color (hex):
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={editingGroupColor.color}
                    onChange={(e) => setEditingGroupColor({ ...editingGroupColor, color: e.target.value })}
                    style={{
                      width: 60,
                      height: 40,
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={editingGroupColor.color}
                    onChange={(e) => {
                      const value = e.target.value
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setEditingGroupColor({ ...editingGroupColor, color: value })
                      }
                    }}
                    placeholder="#000000"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      fontSize: 14,
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                <button 
                  className="btn btn-ghost" 
                  onClick={() => setEditingGroupColor(null)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleChangeGroupColor(editingGroupColor.groupName, editingGroupColor.color, editingGroupColor.subGroupName || null)}
                >
                  Apply Color
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Color Picker Modal */}
      {editingStatusColor && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setEditingStatusColor(null)}>
          <div className="modal" style={{ maxWidth: '400px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">
                Change Color for {editingStatusColor.status === 'AVAILABLE' ? 'Available' : editingStatusColor.status === 'SOLD' ? 'Sold' : 'Locked'}
              </div>
              <button className="btn btn-ghost btn-sm" aria-label="Close" onClick={() => setEditingStatusColor(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ padding: 20 }}>
              <div style={{ marginBottom: 16, fontSize: 14, color: '#6b7280' }}>
                Select a color for this status:
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                {groupColorPalette.map((color) => (
                  <button
                    key={color}
                    className="btn"
                    onClick={() => handleChangeStatusColor(editingStatusColor.status, color)}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      background: color,
                      border: editingStatusColor.color === color ? '3px solid #000' : '2px solid #e5e7eb',
                      borderRadius: 8,
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                    title={color}
                  >
                    {editingStatusColor.color === color && (
                      <span style={{ 
                        position: 'absolute', 
                        top: '50%', 
                        left: '50%', 
                        transform: 'translate(-50%, -50%)',
                        fontSize: 20,
                        color: '#fff',
                        textShadow: '0 0 4px rgba(0,0,0,0.5)'
                      }}>✓</span>
                    )}
                  </button>
                ))}
              </div>
              
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#374151' }}>
                  Or enter a custom color (hex):
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    type="color"
                    value={editingStatusColor.color}
                    onChange={(e) => setEditingStatusColor({ ...editingStatusColor, color: e.target.value })}
                    style={{
                      width: 60,
                      height: 40,
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      cursor: 'pointer'
                    }}
                  />
                  <input
                    type="text"
                    value={editingStatusColor.color}
                    onChange={(e) => {
                      const value = e.target.value
                      if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                        setEditingStatusColor({ ...editingStatusColor, color: value })
                      }
                    }}
                    placeholder="#000000"
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      fontSize: 14,
                      fontFamily: 'monospace'
                    }}
                  />
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
                <button 
                  className="btn btn-ghost" 
                  onClick={() => setEditingStatusColor(null)}
                >
                  Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleChangeStatusColor(editingStatusColor.status, editingStatusColor.color)}
                >
                  Apply Color
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}